import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Tooltips() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">💡 Advanced CSS: Tooltips</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Tooltips display informative messages when users hover over
        elements. They are useful for hints, descriptions, or extra info without
        cluttering the UI.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>position: relative</code> on the parent element.
          </li>
          <li>
            Use <code>position: absolute</code> for the tooltip itself.
          </li>
          <li>
            Hide tooltips by default and show on <code>:hover</code>.
          </li>
          <li>
            Use <code>transition</code> or <code>opacity</code> for smooth
            effects.
          </li>
          <li>
            Tooltips can appear top, bottom, left, or right using{" "}
            <code>top/right/bottom/left</code>.
          </li>
        </ul>
      </div>

      {/* Basic Tooltip */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Basic Tooltip</h2>
        <CodeCopyBlock
          code={`.tooltip {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.tooltip .tooltiptext {
  visibility: hidden;
  background-color: #111;
  color: #fff;
  text-align: center;
  padding: 5px 10px;
  border-radius: 6px;
  position: absolute;
  opacity: 0;
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
  opacity: 1;
  transform: translateY(-5px);
}`}
        />
        <div style={{ marginTop: "1rem" }}>
          <div
            className="tooltip"
            style={{
              padding: "10px",
              backgroundColor: "#3b82f6",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Hover me
            <span className="tooltiptext">Tooltip Text</span>
          </div>
        </div>
      </div>

      {/* Top / Bottom / Left / Right Positions */}
      <div className="tutorial-section">
        <h2 className="section-title">📌 Tooltip Positions</h2>
        <CodeCopyBlock
          code={`.tooltip-top .tooltiptext { bottom: 125%; left: 50%; transform: translateX(-50%); }
.tooltip-bottom .tooltiptext { top: 125%; left: 50%; transform: translateX(-50%); }
.tooltip-left .tooltiptext { right: 125%; top: 50%; transform: translateY(-50%); }
.tooltip-right .tooltiptext { left: 125%; top: 50%; transform: translateY(-50%); }`}
        />
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div
            className="tooltip tooltip-top"
            style={{
              padding: "10px",
              backgroundColor: "#3b82f6",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Top
            <span className="tooltiptext">Tooltip Top</span>
          </div>

          <div
            className="tooltip tooltip-bottom"
            style={{
              padding: "10px",
              backgroundColor: "#f59e0b",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Bottom
            <span className="tooltiptext">Tooltip Bottom</span>
          </div>

          <div
            className="tooltip tooltip-left"
            style={{
              padding: "10px",
              backgroundColor: "#8b5cf6",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Left
            <span className="tooltiptext">Tooltip Left</span>
          </div>

          <div
            className="tooltip tooltip-right"
            style={{
              padding: "10px",
              backgroundColor: "#ef4444",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Right
            <span className="tooltiptext">Tooltip Right</span>
          </div>
        </div>
      </div>

      {/* Animated Tooltip */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Animated Tooltip</h2>
        <CodeCopyBlock
          code={`.tooltip:hover .tooltiptext {
  opacity: 1;
  transform: translateY(-5px);
  transition: opacity 0.4s ease, transform 0.4s ease;
}`}
        />
        <div style={{ marginTop: "1rem" }}>
          <div
            className="tooltip"
            style={{
              padding: "10px",
              backgroundColor: "#10b981",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Hover me
            <span className="tooltiptext">Animated Tooltip</span>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Experiment with different tooltip positions and offsets.</li>
          <li>
            Add <b>arrows</b> using CSS <code>::after</code> pseudo-elements.
          </li>
          <li>
            Combine tooltips with <b>fade, slide, or scale animations</b>.
          </li>
          <li>
            Use tooltips on buttons, icons, or images for interactive UIs.
          </li>
          <li>
            Consider accessibility: add <code>aria-label</code> or{" "}
            <code>role="tooltip"</code>.
          </li>
        </ul>
      </div>
    </div>
  );
}
